/**
 * The Member profile fields screen's URL view state and sort columns (DNKADM-0430 P1-F).
 *
 * URL params on `/member-profile-fields` (all optional; a default is never written):
 * - `q`: search on the field label, its key and its contact-system field key.
 * - `status`: the Active/Inactive/All filter (default `active`).
 * - `sort` + `dir`: the column sort. The server sends fields in no defined order, so the
 *   default is Label ascending.
 */
import { fieldTypeLabel, type MemberProfileField } from '@/api/memberProfileFields';
import { matchesSearch } from '@/components/list/listSearch';
import type { SortState, SortValue } from '@/components/list/listSort';
import { fieldOwnershipLabel } from '@/utils/whitelabel';

export const MEMBER_PROFILE_FIELDS_URL_DEFAULTS = { q: '', status: 'active', sort: '', dir: '' };

export type MemberProfileFieldSortKey =
  | 'label'
  | 'type'
  | 'ownership'
  | 'editable'
  | 'fieldKey'
  | 'status';
export const MEMBER_PROFILE_FIELD_SORT_KEYS: readonly MemberProfileFieldSortKey[] = [
  'label',
  'type',
  'ownership',
  'editable',
  'fieldKey',
  'status',
];
export const MEMBER_PROFILE_FIELD_DEFAULT_SORT: SortState<MemberProfileFieldSortKey> = {
  key: 'label',
  dir: 'asc',
};

/** The field's display label: its label, else its key, else its id. */
export function fieldLabelOf(field: MemberProfileField): string {
  return field.fieldLabel || field.fieldKey || field.id;
}

/**
 * What a field row shows (and sorts by) in each column: the humanized type and
 * ownership labels, the Yes/No chip, the contact-system key (none sorts last), and the
 * Active/Inactive chip.
 */
export function memberProfileFieldSortValue(
  field: MemberProfileField,
  key: MemberProfileFieldSortKey,
): SortValue {
  switch (key) {
    case 'label':
      return fieldLabelOf(field);
    case 'type':
      return fieldTypeLabel(field.fieldType);
    case 'ownership':
      return fieldOwnershipLabel(field.fieldOwnership);
    case 'editable':
      return field.isMemberWritable ? 'Yes' : 'No';
    case 'fieldKey':
      return field.ghlCustomFieldKey;
    case 'status':
      return field.isActive ? 'Active' : 'Inactive';
  }
}

/** The fields search: the label, the field key, or the contact-system field key. */
export function memberProfileFieldMatches(field: MemberProfileField, query: string): boolean {
  return matchesSearch(query, [fieldLabelOf(field), field.fieldKey, field.ghlCustomFieldKey]);
}
